import { useEffect, useState } from 'react';
import '../styles/Home.css';
import GoogleLogin from './GoogleLogin';
import { getActiveGames, getPlayerInfo } from '../tools/PlayerTools';
import { hostGame } from '../tools/GameTools';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner';
import { Helmet } from 'react-helmet-async'; // Import Helmet
import QRCode from 'react-qr-code';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// A simple, inline SVG for the Instagram icon
const InstagramIcon = () => (
    <svg
        className="social-icon-svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
        aria-hidden="true" // Hide from screen readers, aria-label on <a> is used
    >
        <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9 26.3 26.2 58 34.4 93.9 36.2 37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
    </svg>
);

export function Home({ waitForGoLive, goLiveTimestamp, isEarlyAccess, offset }) {

    const [width, setWidth] = useState(window.innerWidth);
    const [currentTimestamp, setCurrentTimestamp] = useState(Date.now());
    const [deviceId, setDeviceId] = useState(null);
    const [profileImageUrl, setProfileImageUrl] = useState(null);

    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        prepareDeviceForQRCode();
        window.addEventListener("resize", handleResize);
        if (waitForGoLive) {
            updateCurrentTimestamp();
        }
        else if (!window.location.href.includes("/join/")) {
            checkExistingGame();
        }
        if (localStorage.getItem('token') != null) {
            const userDetails = jwtDecode(localStorage.getItem('token'));
            setProfileImageUrl(userDetails.picture);
        }
        return () => window.removeEventListener("resize", handleResize);
        // eslint-disable-next-line  
    }, []);

    const prepareDeviceForQRCode = async () => {
        if (localStorage.getItem("token") != null) return;
        const uniqueString = `${navigator.userAgent}-${Date.now()}`;
        const hashedId = await hashString(uniqueString);
        const socket = new WebSocket("wss://dse-web-socket-server.onrender.com");
        socket.onopen = () => {
            socket.send("playerId " + hashedId);
        }
        socket.onmessage = (event) => {
            const details = event.data.split("\n");
            localStorage.setItem("token", details[0]);
            localStorage.setItem("refreshToken", details[1]);
            window.location.reload();
        }
        setDeviceId(hashedId);
    }

    async function hashString(message, algorithm = 'SHA-256') {
        // Encode the string as a Uint8Array
        const msgBuffer = new TextEncoder().encode(message);

        // Hash the message
        const hashBuffer = await crypto.subtle.digest(algorithm, msgBuffer);

        // Convert the ArrayBuffer to a hexadecimal string
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
            .map(b => ('00' + b.toString(16)).slice(-2))
            .join('');

        return hashHex;
    }
    const updateCurrentTimestamp = () => {
        const time = Date.now();
        if (time >= goLiveTimestamp) {
            window.location.reload();
        } else {
            setCurrentTimestamp(time);
            setTimeout(updateCurrentTimestamp, 100);
        }
    }

    const checkExistingGame = () => {
        if (localStorage.getItem("token") != null) {
            setLoading(true);
            getActiveGames().then(data => {
                if (data?.exists) {
                    getPlayerInfo().then(playerInfo => {
                        navigate(`/game/${playerInfo.gameId}`);
                    });
                } else if (data.error && data.error.startsWith("Access from")) {
                    alert(data.error);
                } else {
                    setLoading(false);
                }
            });
        } else {
            setLoading(false);
        }
    }

    const onPostLogin = async () => {
        if (window.location.href.startsWith("https://auth.dummystockexchange.com")) {
            const pcId = window.location.href.substring(window.location.href.lastIndexOf("id=") + 3);
            await axios.post(`${process.env.REACT_APP_SERVER_URL}/auth-from-mobile`,
                {
                    deviceId: pcId,
                    token: localStorage.getItem("token"),
                    refreshToken: localStorage.getItem("refreshToken")
                });
            alert("Login successful, continue on PC");
            localStorage.clear();
            return;
        }
        if (navigator.userAgent.toLowerCase().includes("android") && (!localStorage.getItem("androidAppPopupShownAt") || Date.now() - parseInt(localStorage.getItem("androidAppPopupShownAt")) > 24 * 60 * 60 * 1000)) {
            localStorage.setItem("androidAppPopupShownAt", `${Date.now()}`);
            let redirectPath = window.location.href.substring(window.location.href.indexOf("://") + 4);
            redirectPath = redirectPath.substring(redirectPath.indexOf("/"));
            navigate("/android-device-onboarding?redirectPath=" + encodeURIComponent(redirectPath));
            window.location.reload();
            // window.location.href = "dse://open";
        }
        else window.location.reload();
    }


    const host = async () => {
        setLoading(true);
        try {
            await hostGame();
            checkExistingGame();
        } catch (e) {
            setLoading(false);
        }
    }

    const signOut = () => {
        setLoading(true);
        localStorage.clear();
        window.location.reload();
    }

    // SEO Content
    const pageTitle = "Dummy Stock Exchange - Host & Join Virtual Trading Games";
    const pageDescription = "Log in to Dummy Stock Exchange (DSE) to host a new virtual stock trading game or join an existing one with your friends. Practice trading with dummy money.";

    if (waitForGoLive) {
        if (!goLiveTimestamp) {
            if (width >= 1080) {
                return (
                    <div className='welcome'>
                        <Helmet>
                            <title>{pageTitle}</title>
                            <meta name="description" content={pageDescription} />
                        </Helmet>
                        <div className='background'>
                            <img src="/app_icon.jpg" alt="Dummy Stock Exchange game logo" className="background-img" />
                        </div>
                        <div className='welcome-page-contents'>
                            <p className='heading'>Dummy Stock Exchange</p>
                        </div>
                    </div>
                );
            } else {
                return (
                    <div className='welcome-small'>
                        <Helmet>
                            <title>{pageTitle}</title>
                            <meta name="description" content={pageDescription} />
                        </Helmet>
                        <div className='background-small'>
                            <img src="/app_icon.jpg" alt="Dummy Stock Exchange game logo" className="background-img-small" />
                        </div>
                        <div className='welcome-page-contents-small'>
                            <p className='heading'>Dummy Stock Exchange</p>
                        </div>
                    </div>
                )
            }
        }
        let timeLeft = Math.floor((goLiveTimestamp - currentTimestamp + (offset || 0)) / 1000);
        const seconds = timeLeft % 60;
        timeLeft = Math.floor(timeLeft / 60)
        const minutes = timeLeft % 60;
        timeLeft = Math.floor(timeLeft / 60)
        const hours = timeLeft % 24;
        timeLeft = Math.floor(timeLeft / 24)
        const days = timeLeft;
        if (width >= 1080) {
            return (
                <div className='welcome'>
                    <Helmet>
                        <title>DSE Countdown</title>
                        <meta name="description" content="Dummy Stock Exchange is going live soon! Get ready to trade." />
                    </Helmet>
                    <div className='background'>
                        <img src="/app_icon.jpg" alt="Dummy Stock Exchange game logo" className="background-img" />
                    </div>
                    <div className='welcome-page-contents'>
                        <p className='heading'>Going live in</p>
                        <div style={{ display: "flex", marginBottom: "20px" }}>
                            <div>
                                <p className='heading'>{days.toString().padStart(2, "0")}</p>
                                <div style={{ color: "white", marginTop: "-20px" }}>Days</div>
                            </div>
                            <p className='heading' style={{ marginInline: "10px" }}>:</p>
                            <div>
                                <p className='heading'>{hours.toString().padStart(2, "0")}</p>
                                <div style={{ color: "white", marginTop: "-20px" }}>Hours</div>
                            </div>
                            <p className='heading' style={{ marginInline: "10px" }}>:</p>
                            <div>
                                <p className='heading'>{minutes.toString().padStart(2, "0")}</p>
                                <div style={{ color: "white", marginTop: "-20px" }}>Minutes</div>
                            </div>
                            <p className='heading' style={{ marginInline: "10px" }}>:</p>
                            <div>
                                <p className='heading'>{seconds.toString().padStart(2, "0")}</p>

                                <div style={{ color: "white", marginTop: "-20px" }}>Seconds</div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className='welcome-small'>
                    <Helmet>
                        <title>DSE Countdown</title>
                        <meta name="description" content="Dummy Stock Exchange is going live soon! Get ready to trade." />
                    </Helmet>
                    <div className='background-small'>
                        <img src="/app_icon.jpg" alt="Dummy Stock Exchange game logo" className="background-img-small" />
                    </div>
                    <div className='welcome-page-contents-small'>
                        <p className='heading-small'>Going live in</p>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
                            <div>
                                <p className='heading-small'>{days.toString().padStart(2, "0")}</p>
                                <div style={{ color: "white", marginTop: "-20px" }}>Days</div>
                            </div>
                            <p className='heading-small' style={{ marginInline: "10px" }}>:</p>
                            <div>
                                <p className='heading-small'>{hours.toString().padStart(2, "0")}</p>
                                <div style={{ color: "white", marginTop: "-20px" }}>Hours</div>
                            </div>
                            <p className='heading-small' style={{ marginInline: "10px" }}>:</p>
                            <div>
                                <p className='heading-small'>{minutes.toString().padStart(2, "0")}</p>
                                <div style={{ color: "white", marginTop: "-20px" }}>Minutes</div>
                            </div>
                            <p className='heading-small' style={{ marginInline: "10px" }}>:</p>
                            <div>
                                <p className='heading-small'>{seconds.toString().padStart(2, "0")}</p>
                                <div style={{ color: "white", marginTop: "-20px" }}>Seconds</div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }
    }

    if (width >= 1080) {
        return (
            <div className='welcome'>
                <Helmet>
                    <title>Dummy Stock Exchange | Virtual Stock Market Card Game</title>
                    <meta name="description" content="Master the markets with Dummy Stock Exchange (DSE). A fast-paced, playing card-based stock market game. Host games, trade with friends, and learn investing risk-free." />
                    <link rel="canonical" href="https://dummystockexchange.com" />

                    {/* Open Graph / Facebook */}
                    <meta property="og:type" content="website" />
                    <meta property="og:title" content="Dummy Stock Exchange - The Card-Based Trading Game" />
                    <meta property="og:description" content="Experience the thrill of the stock market in a card game format." />
                    <meta property="og:image" content="https://dummystockexchange.com/app_icon.jpg" />

                    {/* JSON-LD Structured Data - This is CRITICAL for Google */}
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "SoftwareApplication",
                            "name": "Dummy Stock Exchange",
                            "operatingSystem": "Web",
                            "applicationCategory": "GameApplication",
                            "description": "A card-based virtual stock trading game to practice trading with friends.",
                            "offers": {
                                "@type": "Offer",
                                "price": "0",
                                "priceCurrency": "USD"
                            }
                        })}
                    </script>
                </Helmet>
                <div className='background'>
                    <img src="/app_icon.jpg" alt="Dummy Stock Exchange game logo" className="background-img" />
                </div>
                <div className='welcome-page-contents'>
                    <h1 className='heading'>{isEarlyAccess ? "DSE (Early Access)" : "Dummy Stock Exchange"}</h1>
                    {localStorage.getItem("token") != null ?
                        <div className='buttons-container'>
                            {loading && <Spinner />}
                            {!loading && <button className='btn-homepage' onClick={host}>Host a game</button>}
                            {!loading && <button className='btn-homepage' onClick={signOut}>
                                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                                    {profileImageUrl ? <img src={profileImageUrl} alt='player-profile' width={30} height={30} style={{ marginRight: '10px', borderRadius: '50%' }}></img> : <div></div>}
                                    Sign out
                                </div>
                            </button>}
                        </div>
                        : <div className='buttons-container'>
                            {deviceId != null && <QRCode value={`https://auth.dummystockexchange.com?id=${deviceId}`} size={128} bgColor='#000000' fgColor='#FFFFFF' />}
                            <div style={{ height: '20px' }}></div>
                            <p style={{ color: "white", marginBottom: "20px" }}>Scan the QR code to log in from your mobile device</p>
                            <div style={{ height: '20px' }}></div>
                            <GoogleLogin onPostLogin={onPostLogin} style={{ width: "25vw" }} />
                        </div>
                    }
                    <div className="social-links-container">
                        <a
                            href="https://www.instagram.com/dse__108"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-link"
                            aria-label="Follow Dummy Stock Exchange on Instagram"
                        >
                            <InstagramIcon />
                        </a>
                    </div>
                </div>
            </div>
        );
    } else {
        return (
            <div className='welcome-small'>
                <Helmet>
                    <title>Dummy Stock Exchange | Virtual Stock Market Card Game</title>
                    <meta name="description" content="Master the markets with Dummy Stock Exchange (DSE). A fast-paced, playing card-based stock market game. Host games, trade with friends, and learn investing risk-free." />
                    <link rel="canonical" href="https://dummystockexchange.com" />

                    {/* Open Graph / Facebook */}
                    <meta property="og:type" content="website" />
                    <meta property="og:title" content="Dummy Stock Exchange - The Card-Based Trading Game" />
                    <meta property="og:description" content="Experience the thrill of the stock market in a card game format." />
                    <meta property="og:image" content="https://dummystockexchange.com/app_icon.jpg" />

                    {/* JSON-LD Structured Data - This is CRITICAL for Google */}
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "SoftwareApplication",
                            "name": "Dummy Stock Exchange",
                            "operatingSystem": "Web",
                            "applicationCategory": "GameApplication",
                            "description": "A card-based virtual stock trading game to practice trading with friends.",
                            "offers": {
                                "@type": "Offer",
                                "price": "0",
                                "priceCurrency": "USD"
                            }
                        })}
                    </script>
                </Helmet>
                <div className='background-small'>
                    <img src="/app_icon.jpg" alt="Dummy Stock Exchange game logo" className="background-img-small" />
                </div>
                <div className='welcome-page-contents-small'>
                    <h1 className='heading-small'>{isEarlyAccess ? "DSE (Early Access)" : "Dummy Stock Exchange"}</h1>
                    {localStorage.getItem("token") != null ?
                        <div className='buttons-container'>
                            {loading && <Spinner />}
                            {!loading && <button className='btn-homepage-small' onClick={host}>Host a game</button>}
                            {!loading && <button className='btn-homepage-small' onClick={signOut}>
                                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                                    {profileImageUrl ? <img src={profileImageUrl} alt='player-profile' width={30} height={30} style={{ marginRight: '10px', borderRadius: '50%' }}></img> : <div></div>}
                                    Sign out
                                </div>
                            </button>}
                        </div>
                        : <GoogleLogin onPostLogin={onPostLogin} />
                    }
                    <div className="social-links-container">
                        <a
                            href="https://www.instagram.com/dse__108"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-link"
                            aria-label="Follow Dummy Stock Exchange on Instagram"
                        >
                            <InstagramIcon />
                        </a>
                    </div>
                </div>
            </div>
        )
    }
}